import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { flushSync } from "react-dom";
import { useReducedMotion } from "motion/react";
import { ANVIL_MATERIALIZE_ASSETS, ANVIL_MATERIALIZE_STYLES, seededAnvilMaterializeStyle, type AnvilLoopName, type AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
import { ANVIL_COFFEE_TIMINGS, ANVIL_MATERIALIZE_FRAMES, ANVIL_TRANSITION_MANIFEST, type AnvilMaterializeStyle, type AnvilTransitionVariant } from "@repo/blacksmith-ui/anvil/manifest";
import { useStableCallback } from "@/hooks/useStableCallback";
import { assetUrl } from "@/lib/assetUrl";

/** A phase still loading this long after it is due hard-swaps to the target loop. */
const LOAD_TIMEOUT_MS = 1_000;
// A swap paints up to one 60Hz frame after setState, and each phase's last
// frame repeats as the next phase's first, so seams are issued one
// generator tick (FRAME_MS in scripts/generate-anvil-variants.mjs) early.
const SWAP_PAINT_LEAD_MS = 17;
export const FRAME_MS = 30;

/** The still pose every outro ends on and every intro starts from. */
const NEUTRAL = "neutral";
/** Nothing on screen: the materialize strip's blank first frame. */
const EMPTY = "empty";
const HOLD = "caffeinating-hold";
const SIP = "caffeinating-sip";

/** The longest materialize strip, for the presence exit cap. */
export const ANVIL_MATERIALIZE_MS = Math.max(...Object.values(ANVIL_MATERIALIZE_FRAMES)) * FRAME_MS;
type StripDirection = "in" | "out";

/**
 * Steps an img showing a materialize strip through its frames (keyframes in
 * anvil.css), `offsetMs` in; with no direction it rests on the blank frame.
 * The two directions need distinct keyframes: changing only the delay of a
 * running animation re-times it instead of restarting it.
 */
export function materializeStripStyle(style: AnvilMaterializeStyle, direction: StripDirection | null, offsetMs = 0): CSSProperties {
  const frames = ANVIL_MATERIALIZE_FRAMES[style];
  // `cover` scales the strip to the img's height; object-position k/(n-1)
  // then shows frame k. The aspect ratio keeps the box square: Preflight's
  // `height: auto` would otherwise take the strip's 15:1 ratio.
  const frame: CSSProperties = {
    objectFit: "cover",
    objectPosition: "0% 0",
    aspectRatio: "1 / 1"
  };
  if (!direction) return frame;
  const name = direction === "in" ? "anvil-materialize" : "anvil-dematerialize";
  return {
    ...frame,
    animation: `${name} ${frames * FRAME_MS}ms steps(${frames}, jump-none) ${-offsetMs}ms both`
  };
}
type Pose = AnvilLoopName | typeof NEUTRAL | typeof EMPTY;
const isLoop = (pose: Pose | null): pose is AnvilLoopName => pose !== null && pose !== NEUTRAL && pose !== EMPTY;

/** A pose with intro/outro clips, or neutral, which needs neither. */
type ChainPose = AnvilTransitionVariant | typeof NEUTRAL;
interface Exit {
  startMs: number;
  endMs: number;
  outro: number;
  outroMs: number;
}
interface Shown {
  pose: Pose;
  theme: AnvilTheme;
  src: string;
  phase: "loop" | "outro" | "intro";
  /** Set while `src` is the materialize strip playing: which way, and how far in it started. */
  strip?: {
    style: AnvilMaterializeStyle;
    direction: StripDirection;
    offsetMs: number;
  };
}
const blobs = new Map<string, Promise<Blob>>();

/** One fetch per animation asset, shared by every player that needs it. */
export function fetchBlob(path: string): Promise<Blob> {
  let blob = blobs.get(path);
  if (!blob) {
    blob = fetch(path).then(response => {
      if (!response.ok) throw new Error(`${response.status} ${path}`);
      return response.blob();
    });
    blob.catch(() => blobs.delete(path));
    blobs.set(path, blob);
  }
  return blob;
}
export function animationPath(name: string, theme: AnvilTheme): string {
  return assetUrl(`anvil-animations/anvil-${name}-${theme}.png`);
}

/** The PNG a pose rests on; empty rests on the materialize strip's blank frame. */
function posePath(pose: Pose, theme: AnvilTheme, materializeStyle: AnvilMaterializeStyle): string {
  return animationPath(pose === EMPTY ? `materialize-${materializeStyle}` : pose, theme);
}
function shownStyle(shown: Shown | null, materializeStyle: AnvilMaterializeStyle): CSSProperties | undefined {
  if (shown?.strip) {
    return materializeStripStyle(shown.strip.style, shown.strip.direction, shown.strip.offsetMs);
  }
  if (shown?.pose === EMPTY) {
    return materializeStripStyle(materializeStyle, null);
  }
  return undefined;
}

/**
 * A fresh object URL for `blob`, so a play-once APNG starts at frame 0,
 * decoded before it is shown: swapping an img to a URL that isn't ready yet
 * can paint the outgoing image's first frame in between.
 */
export async function decodedSrc(blob: Blob): Promise<string> {
  const src = URL.createObjectURL(blob);
  const img = new Image();
  img.src = src;
  try {
    await img.decode();
  } catch (error) {
    URL.revokeObjectURL(src);
    throw error;
  }
  return src;
}
interface Decoding {
  src: string;
  failed: boolean;
}

/** Like `decodedSrc`, but returns at once so a swap never waits on the decode. */
function startDecode(blob: Blob): Decoding {
  const decoding = {
    src: URL.createObjectURL(blob),
    failed: false
  };
  const img = new Image();
  img.src = decoding.src;
  img.decode().catch(() => {
    decoding.failed = true;
  });
  return decoding;
}
export function revokeAll(srcs: string[]): void {
  for (const src of srcs) URL.revokeObjectURL(src);
}

/** Rejects if any blob fails to decode, revoking the ones that did. */
async function decodedSrcs(blobs: Blob[]): Promise<string[]> {
  const results = await Promise.allSettled(blobs.map(decodedSrc));
  const srcs = results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
  if (srcs.length < results.length) {
    revokeAll(srcs);
    throw new Error("anvil animation failed to decode");
  }
  return srcs;
}
export function revokeLater(src: string): void {
  if (!src.startsWith("blob:")) return;
  // After the img has swapped off it.
  setTimeout(() => URL.revokeObjectURL(src), 1_000);
}

/** Every outro of `pose` by `Exit.outro`, named as the generator writes them. */
function outroNames(pose: AnvilTransitionVariant): string[] {
  const exits: readonly Exit[] = ANVIL_TRANSITION_MANIFEST[pose].exits;
  const count = Math.max(...exits.map(exit => exit.outro)) + 1;
  return Array.from({
    length: count
  }, (_, outro) => outro === 0 ? `${pose}-outro` : `${pose}-outro-${outro}`);
}

/** What a resting pose may hand off to next, for prefetch. */
function nextAssets(pose: Pose): string[] {
  if (pose === EMPTY) return [...ANVIL_MATERIALIZE_ASSETS, NEUTRAL];
  if (pose === NEUTRAL) return ANVIL_MATERIALIZE_ASSETS;
  if (pose === SIP) return [HOLD, ...outroNames(HOLD)];
  if (pose === HOLD) return [SIP, ...outroNames(HOLD)];
  return outroNames(pose);
}

/**
 * The next pose on the way from `from` to `to`. Empty only connects to
 * neutral, through the strip. The sip only connects to the held mug: it ends
 * on the mug's first frame, and the mug's loop seam is where the drink
 * starts.
 */
function nextHop(from: Pose, to: Pose): Pose {
  if (from === SIP) return HOLD;
  if (from === EMPTY) return NEUTRAL;
  if (to === EMPTY) return from === NEUTRAL ? EMPTY : NEUTRAL;
  if (to === SIP && from !== HOLD) return HOLD;
  return to;
}

/**
 * The next outro the loop `from` reaches `intoLoopMs` in with time left to
 * paint the swap, e.g. running (exits at 0, 480, 960 of 1440) 100ms in → the
 * 480 one, 380ms away. `outro` indexes `outroNames(from)`.
 */
export function nextOutro(from: AnvilTransitionVariant, intoLoopMs: number): {
  outro: number;
  exitInMs: number;
  outroMs: number;
} {
  const {
    loopMs
  } = ANVIL_TRANSITION_MANIFEST[from];
  const exits: readonly Exit[] = ANVIL_TRANSITION_MANIFEST[from].exits;
  const intoLoop = intoLoopMs % loopMs;
  const exit = exits.find(candidate => intoLoop + SWAP_PAINT_LEAD_MS <= candidate.endMs);
  const {
    outro,
    outroMs
  } = exit ?? exits[0];
  const exitInMs = exit ? Math.max(0, exit.startMs - intoLoop) : loopMs - intoLoop;
  return {
    outro,
    exitInMs,
    outroMs
  };
}

/** What follows an outro on the way to `to`: its intro and loop, or neutral itself. */
const arrivalNames = (to: ChainPose): string[] => to === NEUTRAL ? [NEUTRAL] : [`${to}-intro`, to];

/**
 * Fresh decoded srcs for leaving `from` into `to`: every outro of `from` by
 * `nextOutro`'s index, then `to`'s intro and loop, or the neutral pose when
 * `to` is null. Rejects if any fails to load or decode.
 */
export function decodeExitClips(from: AnvilTransitionVariant, to: AnvilTransitionVariant | null, theme: AnvilTheme): Promise<string[]> {
  const names = [...outroNames(from), ...arrivalNames(to ?? NEUTRAL)];
  return Promise.all(names.map(name => fetchBlob(animationPath(name, theme)))).then(decodedSrcs);
}

/** Warms what a handoff leaving `loop` may start with: its outros, and the neutral pose. */
export function prefetchOutros(loop: AnvilTransitionVariant, theme: AnvilTheme): void {
  for (const name of [...nextAssets(loop), NEUTRAL]) {
    void fetchBlob(animationPath(name, theme));
  }
}
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, Math.max(0, ms)));
export function withLoadTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([promise, wait(LOAD_TIMEOUT_MS).then(() => {
    throw new Error("anvil transition load timed out");
  })]);
}

/** `withLoadTimeout` for decoded srcs, revoking any that land after the timeout. */
function decodedInTime<T extends string | string[]>(decoding: Promise<T>): Promise<T> {
  return withLoadTimeout(decoding).catch((error: unknown) => {
    void decoding.then(late => revokeAll(([] as string[]).concat(late)), () => {});
    throw error;
  });
}
const nextPaint = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/** Entrance keys whose materialize → intro beat has already played. */
const entered = new Set<string>();
/** Row relocation can remount an appearance after its turn seed has changed. */
const appearanceStyles = new Map<string, AnvilMaterializeStyle>();

/**
 * The src for an anvil that animates every change of `target` instead of
 * cutting: `loop → outro → neutral → intro → loop`, e.g. sleuthing →
 * sleuthing-outro → type-intro → type. The held mug and the sip hand off
 * directly at their shared frame. `src` is null while `target` is; a theme
 * switch shows the target directly. Apply `style` to the img alongside
 * `src`: it steps the materialize strip.
 *
 * - `entranceKey`: the first mount with a key starts from nothing,
 *   materializes into the neutral pose and plays the target's intro, so a
 *   turn's Codey pops in; remounts with the same key (the row relocating)
 *   show the target directly.
 * - `settle`: heads back to the neutral pose.
 * - `vanish`: heads back to the neutral pose and dematerializes from there,
 *   then calls `onVanished`. Clearing it mid-vanish reverses from the frame
 *   on screen.
 *
 * Reduced motion skips all of it and swaps targets directly; a vanish is
 * instant.
 * `holding` is true from a target change until its outro starts, so a label
 * that changed with the target can roll in step with the character. `loop`
 * is the loop resting on screen while it is still the target, for an overlay
 * layered behind it (useAnvilOverlay). `landingLoop` is that loop unless it
 * is the sip, for a handoff to land on (useAnvilHandoff): `adoptLoop` shows
 * a fresh decoded copy of it from frame 0, and revokes the copy instead
 * once Codey has moved on from that loop.
 */
export function useAnvilTransition(loop, theme, t0) {
  const $ = _c(73);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    seed,
    entranceKey,
    settle: t2,
    vanish: t3,
    onVanished
  } = t1;
  const settle = t2 === undefined ? false : t2;
  const vanish = t3 === undefined ? false : t3;
  const reduceMotion = useReducedMotion() ?? false;
  let t4;
  if ($[2] !== entranceKey || $[3] !== seed) {
    t4 = () => {
      const existing = entranceKey !== undefined ? appearanceStyles.get(entranceKey) : undefined;
      if (existing) {
        return existing;
      }
      const appearanceSeed = seed ?? entranceKey;
      const style = appearanceSeed !== undefined ? seededAnvilMaterializeStyle(appearanceSeed) : ANVIL_MATERIALIZE_STYLES[Math.floor(Math.random() * ANVIL_MATERIALIZE_STYLES.length)];
      if (entranceKey !== undefined) {
        appearanceStyles.set(entranceKey, style);
      }
      return style;
    };
    $[2] = entranceKey;
    $[3] = seed;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [materializeStyle] = useState(t4);
  let target;
  if ($[5] !== loop || $[6] !== reduceMotion || $[7] !== settle || $[8] !== vanish) {
    target = loop;
    if (loop !== null && vanish) {
      target = EMPTY;
    } else {
      if (loop !== null && settle && !reduceMotion) {
        target = NEUTRAL;
      }
    }
    $[5] = loop;
    $[6] = reduceMotion;
    $[7] = settle;
    $[8] = vanish;
    $[9] = target;
  } else {
    target = $[9];
  }
  let t5;
  if ($[10] !== entranceKey || $[11] !== reduceMotion) {
    t5 = () => entranceKey !== undefined && !reduceMotion && !entered.has(entranceKey);
    $[10] = entranceKey;
    $[11] = reduceMotion;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [entering, setEntering] = useState(t5);
  const [shown, setShown] = useState(null);
  const shownRef = useRef(null);
  let t6;
  if ($[13] !== target || $[14] !== theme) {
    t6 = {
      target,
      theme
    };
    $[13] = target;
    $[14] = theme;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const latestRef = useRef(t6);
  const loopStartRef = useRef(0);
  const sipStartRef = useRef(0);
  const runningRef = useRef(false);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const wakeRef = useRef(null);
  let t7;
  if ($[16] !== target || $[17] !== theme) {
    t7 = () => {
      latestRef.current = {
        target,
        theme
      };
    };
    $[16] = target;
    $[17] = theme;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useLayoutEffect(t7);
  const resting = shown?.theme === theme ? shown : null;
  const arrivalPose = target === EMPTY && !reduceMotion ? NEUTRAL : target;
  const restingPose = entering ? EMPTY : arrivalPose;
  let t8;
  if ($[19] !== materializeStyle || $[20] !== resting || $[21] !== restingPose || $[22] !== target || $[23] !== theme) {
    t8 = target === null || restingPose === null ? null : resting ?? {
      pose: restingPose,
      theme,
      src: posePath(restingPose, theme, materializeStyle),
      phase: "loop"
    };
    $[19] = materializeStyle;
    $[20] = resting;
    $[21] = restingPose;
    $[22] = target;
    $[23] = theme;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const current = t8;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = next => {
      const previous = shownRef.current;
      shownRef.current = next;
      setShown(next);
      if (previous && previous.src !== next?.src) {
        revokeLater(previous.src);
      }
    };
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const show = useStableCallback(t9);
  let t10;
  if ($[26] !== arrivalPose || $[27] !== entering || $[28] !== entranceKey || $[29] !== materializeStyle || $[30] !== reduceMotion || $[31] !== show || $[32] !== target || $[33] !== theme) {
    t10 = () => {
      const hardSwap = next_0 => {
        generationRef.current = generationRef.current + 1;
        runningRef.current = false;
        show(next_0);
      };
      if (target === null) {
        if (shownRef.current) {
          hardSwap(null);
        }
        return;
      }
      let swappedMidRun = false;
      if (shownRef.current?.theme !== theme || reduceMotion && shownRef.current?.pose !== target) {
        const entrance = shownRef.current === null && entering;
        if (entrance) {
          entered.add(entranceKey);
          setEntering(false);
        }
        const pose = entrance ? EMPTY : arrivalPose;
        hardSwap({
          pose,
          theme,
          src: posePath(pose, theme, materializeStyle),
          phase: "loop"
        });
        loopStartRef.current = sipStartRef.current = performance.now();
        if (pose === target) {
          return;
        }
        swappedMidRun = !entrance;
      }
      wakeRef.current?.();
      if (shownRef.current.pose === target || runningRef.current) {
        return;
      }
      const generation = generationRef.current;
      const stale = () => !mountedRef.current || generationRef.current !== generation || latestRef.current.target === null || latestRef.current.theme !== theme;
      const fetchAll = names => Promise.all(names.map(name => fetchBlob(animationPath(name, theme))));
      const chain = (from, to) => {
        const nextBlobs = fetchAll(arrivalNames(to));
        nextBlobs.catch(_temp);
        let next_1;
        let nextTaken = false;
        const play = async () => {
          if (from !== NEUTRAL) {
            const outros = await withLoadTimeout(fetchAll(outroNames(from)));
            if (stale() || latestRef.current.target === from) {
              return false;
            }
            const exit = nextOutro(from, performance.now() - loopStartRef.current);
            const outro = startDecode(outros[exit.outro]);
            await wait(exit.exitInMs - SWAP_PAINT_LEAD_MS);
            if (stale() || latestRef.current.target === from) {
              URL.revokeObjectURL(outro.src);
              return false;
            }
            if (outro.failed) {
              URL.revokeObjectURL(outro.src);
              throw new Error("anvil outro failed to decode");
            }
            show({
              pose: from,
              theme,
              src: outro.src,
              phase: "outro"
            });
            await nextPaint();
            next_1 = nextBlobs.then(decodedSrcs);
            next_1.catch(_temp2);
            await wait(exit.outroMs - FRAME_MS);
            if (!stale() && to !== NEUTRAL && latestRef.current.target !== to) {
              const [neutral] = await decodedInTime(fetchAll([NEUTRAL]).then(decodedSrcs));
              if (stale()) {
                revokeAll([neutral]);
                return false;
              }
              show({
                pose: NEUTRAL,
                theme,
                src: neutral,
                phase: "loop"
              });
              await nextPaint();
              return true;
            }
          } else {
            next_1 = nextBlobs.then(decodedSrcs);
            next_1.catch(_temp3);
          }
          const srcs = await withLoadTimeout(next_1);
          nextTaken = true;
          if (stale()) {
            revokeAll(srcs);
            return false;
          }
          if (to === NEUTRAL) {
            show({
              pose: NEUTRAL,
              theme,
              src: srcs[0],
              phase: "loop"
            });
            await nextPaint();
            return true;
          }
          const [intro, loopSrc] = srcs as [string, string];
          show({
            pose: from,
            theme,
            src: intro,
            phase: "intro"
          });
          await nextPaint();
          await wait(ANVIL_TRANSITION_MANIFEST[to].introMs - FRAME_MS);
          if (stale()) {
            URL.revokeObjectURL(loopSrc);
            return false;
          }
          show({
            pose: to,
            theme,
            src: loopSrc,
            phase: "loop"
          });
          await nextPaint();
          loopStartRef.current = performance.now();
          return true;
        };
        return play().finally(() => {
          if (!nextTaken) {
            next_1?.then(revokeAll, _temp4);
          }
        });
      };
      const sipIn = async () => {
        const blob = await withLoadTimeout(fetchBlob(animationPath(SIP, theme)));
        const retarget = () => latestRef.current.target !== HOLD;
        if (stale()) {
          return false;
        }
        if (latestRef.current.target !== SIP) {
          return retarget();
        }
        const {
          loopMs
        } = ANVIL_TRANSITION_MANIFEST[HOLD];
        const toSeam = loopMs - (performance.now() - loopStartRef.current) % loopMs;
        const sip = startDecode(blob);
        await wait((toSeam < SWAP_PAINT_LEAD_MS ? toSeam + loopMs : toSeam) - SWAP_PAINT_LEAD_MS);
        if (stale() || latestRef.current.target !== SIP) {
          URL.revokeObjectURL(sip.src);
          return !stale() && retarget();
        }
        if (sip.failed) {
          URL.revokeObjectURL(sip.src);
          throw new Error("anvil sip failed to decode");
        }
        show({
          pose: SIP,
          theme,
          src: sip.src,
          phase: "loop"
        });
        await nextPaint();
        sipStartRef.current = performance.now();
        return true;
      };
      const sipOut = async () => {
        const blob_0 = await withLoadTimeout(fetchBlob(animationPath(HOLD, theme)));
        const mug = await decodedInTime(decodedSrc(blob_0));
        await wait(sipStartRef.current + ANVIL_COFFEE_TIMINGS.sipMs - FRAME_MS - SWAP_PAINT_LEAD_MS - performance.now());
        if (stale() || latestRef.current.target === SIP) {
          URL.revokeObjectURL(mug);
          return false;
        }
        show({
          pose: HOLD,
          theme,
          src: mug,
          phase: "loop"
        });
        await nextPaint();
        loopStartRef.current = performance.now();
        return true;
      };
      const waitForRetarget = ms => new Promise(resolve => {
        const wake = () => {
          clearTimeout(timer);
          if (wakeRef.current === wake) {
            wakeRef.current = null;
          }
          resolve();
        };
        const timer = setTimeout(wake, Math.max(0, ms));
        wakeRef.current = wake;
      });
      const materialize = async direction => {
        const durationMs = ANVIL_MATERIALIZE_FRAMES[materializeStyle] * FRAME_MS;
        const blob_1 = await withLoadTimeout(fetchBlob(animationPath(`materialize-${materializeStyle}`, theme)));
        if (stale()) {
          return false;
        }
        const src = await decodedInTime(decodedSrc(blob_1));
        const wants = () => latestRef.current.target === EMPTY ? "out" : "in";
        if (stale() || wants() !== direction) {
          URL.revokeObjectURL(src);
          return !stale();
        }
        if (swappedMidRun) {
          swappedMidRun = false;
          await nextPaint();
          await nextPaint();
          if (stale() || wants() !== direction) {
            URL.revokeObjectURL(src);
            return !stale();
          }
        }
        let way = direction;
        let offsetMs = 0;
        while (true) {
          const endsAt = performance.now() + durationMs - offsetMs;
          show({
            pose: way === "in" ? EMPTY : NEUTRAL,
            theme,
            src,
            phase: way === "in" ? "intro" : "outro",
            strip: {
              style: materializeStyle,
              direction: way,
              offsetMs
            }
          });
          while (wants() === way && performance.now() < endsAt) {
            await waitForRetarget(endsAt - performance.now());
            if (stale()) {
              return false;
            }
          }
          if (wants() === way) {
            break;
          }
          offsetMs = Math.max(0, endsAt - performance.now());
          way = way === "in" ? "out" : "in";
        }
        show({
          pose: way === "in" ? NEUTRAL : EMPTY,
          theme,
          src,
          phase: "loop",
          strip: {
            style: materializeStyle,
            direction: way,
            offsetMs
          }
        });
        return true;
      };
      const step = (from_0, to_0) => {
        if (from_0 === EMPTY || to_0 === EMPTY) {
          return materialize(to_0 === EMPTY ? "out" : "in");
        }
        if (from_0 === SIP) {
          return sipOut();
        }
        if (from_0 === HOLD && to_0 === SIP) {
          return sipIn();
        }
        return chain(from_0 as ChainPose, to_0 as ChainPose);
      };
      runningRef.current = true;
      const run = async () => {
        while (true) {
          const from_1 = shownRef.current;
          const to_1 = latestRef.current.target;
          if (!from_1 || to_1 === null || from_1.pose === to_1) {
            return;
          }
          const hop = nextHop(from_1.pose, to_1);
          const advanced = await step(from_1.pose, hop).catch(() => {
            if (stale()) {
              return false;
            }
            show({
              pose: hop,
              theme,
              src: posePath(hop, theme, materializeStyle),
              phase: "loop"
            });
            loopStartRef.current = sipStartRef.current = performance.now();
            return true;
          });
          if (!advanced) {
            return;
          }
        }
      };
      run().finally(() => {
        if (generationRef.current === generation) {
          runningRef.current = false;
        }
      });
    };
    $[26] = arrivalPose;
    $[27] = entering;
    $[28] = entranceKey;
    $[29] = materializeStyle;
    $[30] = reduceMotion;
    $[31] = show;
    $[32] = target;
    $[33] = theme;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== arrivalPose || $[36] !== entering || $[37] !== entranceKey || $[38] !== materializeStyle || $[39] !== reduceMotion || $[40] !== show || $[41] !== target || $[42] !== theme) {
    t11 = [target, arrivalPose, theme, show, entering, entranceKey, reduceMotion, materializeStyle];
    $[35] = arrivalPose;
    $[36] = entering;
    $[37] = entranceKey;
    $[38] = materializeStyle;
    $[39] = reduceMotion;
    $[40] = show;
    $[41] = target;
    $[42] = theme;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  useEffect(t10, t11);
  const vanished = target === EMPTY && current?.pose === EMPTY && current.phase === "loop";
  let t12;
  if ($[44] !== onVanished) {
    t12 = () => onVanished?.();
    $[44] = onVanished;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const notifyVanished = useStableCallback(t12);
  let t13;
  let t14;
  if ($[46] !== notifyVanished || $[47] !== vanished) {
    t13 = () => {
      if (vanished) {
        notifyVanished();
      }
    };
    t14 = [vanished, notifyVanished];
    $[46] = notifyVanished;
    $[47] = vanished;
    $[48] = t13;
    $[49] = t14;
  } else {
    t13 = $[48];
    t14 = $[49];
  }
  useEffect(t13, t14);
  const restingLoop = current?.phase === "loop" ? current.pose : null;
  let t15;
  let t16;
  if ($[50] !== restingLoop || $[51] !== theme) {
    t15 = () => {
      if (!restingLoop) {
        return;
      }
      for (const name_0 of nextAssets(restingLoop)) {
        fetchBlob(animationPath(name_0, theme));
      }
    };
    t16 = [restingLoop, theme];
    $[50] = restingLoop;
    $[51] = theme;
    $[52] = t15;
    $[53] = t16;
  } else {
    t15 = $[52];
    t16 = $[53];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (shownRef.current) {
          revokeLater(shownRef.current.src);
        }
      };
    };
    t18 = [];
    $[54] = t17;
    $[55] = t18;
  } else {
    t17 = $[54];
    t18 = $[55];
  }
  useEffect(t17, t18);
  const holding = current !== null && current.phase === "loop" && current.pose !== target;
  let t19;
  if ($[56] !== holding || $[57] !== restingLoop) {
    t19 = isLoop(restingLoop) && !holding ? restingLoop : null;
    $[56] = holding;
    $[57] = restingLoop;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const restingOn = t19;
  const landingLoop = restingOn === SIP ? null : restingOn;
  let t20;
  if ($[59] !== landingLoop || $[60] !== show || $[61] !== theme) {
    t20 = src_0 => {
      const onScreen = shownRef.current;
      if (landingLoop === null || runningRef.current || latestRef.current.target !== landingLoop || latestRef.current.theme !== theme || onScreen?.phase !== "loop" || onScreen.pose !== landingLoop || onScreen.theme !== theme) {
        URL.revokeObjectURL(src_0);
        return;
      }
      flushSync(() => show({
        pose: landingLoop,
        theme,
        src: src_0,
        phase: "loop"
      }));
      loopStartRef.current = performance.now();
    };
    $[59] = landingLoop;
    $[60] = show;
    $[61] = theme;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  const adoptLoop = t20;
  const t21 = current?.src ?? null;
  let t22;
  if ($[63] !== current || $[64] !== materializeStyle) {
    t22 = shownStyle(current, materializeStyle);
    $[63] = current;
    $[64] = materializeStyle;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== adoptLoop || $[67] !== holding || $[68] !== landingLoop || $[69] !== restingOn || $[70] !== t21 || $[71] !== t22) {
    t23 = {
      src: t21,
      style: t22,
      holding,
      loop: restingOn,
      landingLoop,
      adoptLoop
    };
    $[66] = adoptLoop;
    $[67] = holding;
    $[68] = landingLoop;
    $[69] = restingOn;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  return t23;
}
function _temp4() {}
function _temp3() {}
function _temp2() {}
function _temp() {}